import React from 'react';
import { Box, IconButton, TableCell, TableRow, Typography } from '@mui/material';
import { Visibility, Warning, ArrowUpward, Circle, ArrowDownward } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { ROUTES } from 'module/navigation/enums/routes.enums';
import { formatDateMMDDYYYY } from 'shared/utils/dateUtils';
import { markBroadcastAsRead } from 'core/API/broadcast';
import { useMutation, useQueryClient } from 'react-query';
import EmployeeWidgetCard from './EmployeeWidgetCard';

interface BroadcastMessage {
	id: number;
	title?: string;
	subject?: string;
	isRead?: boolean;
	priority?: { label?: string; name?: string };
	sender?: {
		firstName?: string;
		middleName?: string;
		lastName?: string;
	};
	receiverUser?: {
		firstName?: string;
		middleName?: string;
		lastName?: string;
	};
	receiverGroup?: { groupName?: string };
	createdDate?: string;
	createdTime?: string;
	createdAt?: string;
	created_at?: string;
	entireOrgCreatedDate?: string;
	entireOrgCreatedTime?: string;
}

interface EmployeeBroadcastWidgetProps {
	loading: boolean;
	items: BroadcastMessage[];
	workplaceId: number;
}

const getUserFullName = (user?: BroadcastMessage['sender']): string => {
	if (!user) return 'Unknown User';
	const parts = [user.firstName, user.middleName, user.lastName].filter(
		(part) => !!part && part.trim() !== '',
	);
	return parts.join(' ') || 'Unknown User';
};

const isOrganizationBroadcast = (message: BroadcastMessage): boolean =>
	Boolean(message.entireOrgCreatedDate || message.entireOrgCreatedTime);

const getDisplayCreatedFields = (
	message: BroadcastMessage,
): { createdDate?: string; createdTime?: string } => {
	const useEntireOrgFields = isOrganizationBroadcast(message);
	return {
		createdDate: useEntireOrgFields
			? (message.entireOrgCreatedDate ?? message.createdDate)
			: message.createdDate,
		createdTime: useEntireOrgFields
			? (message.entireOrgCreatedTime ?? message.createdTime)
			: message.createdTime,
	};
};

const formatDateFromAPI = (createdDate?: string | null): string => {
	if (!createdDate) return 'N/A';
	const formatted = formatDateMMDDYYYY(createdDate);
	return formatted !== 'N/A' ? formatted : 'N/A';
};

const formatTimeFromAPI = (createdTime?: string | null): string => {
	if (!createdTime) return 'N/A';

	const timeParts = createdTime.split(':');
	if (timeParts.length >= 2) {
		const hour = Number(timeParts[0]) || 0;
		const minute = Number(timeParts[1]) || 0;
		const second = timeParts.length >= 3 ? Number(timeParts[2]) || 0 : 0;
		const date = new Date();
		date.setHours(hour, minute, second, 0);
		return date.toLocaleTimeString('en-US', {
			hour: 'numeric',
			minute: '2-digit',
			second: '2-digit',
			hour12: true,
		});
	}

	return createdTime;
};

const getReceiverName = (message: BroadcastMessage): string => {
	if (message.receiverUser) return getUserFullName(message.receiverUser);
	if (message.receiverGroup?.groupName) return message.receiverGroup.groupName;
	return 'Unknown User';
};

const getPriorityIcon = (priorityLabel?: string | null) => {
	const label = (priorityLabel || '').toLowerCase();
	if (label === 'critical' || label === 'urgent') {
		return {
			icon: <Warning sx={{ color: '#d32f2f', fontSize: '18px' }} />,
			name: 'Urgent',
			color: '#d32f2f',
		};
	}
	if (label === 'high') {
		return {
			icon: <ArrowUpward sx={{ color: '#f57c00', fontSize: '18px' }} />,
			name: 'High',
			color: '#f57c00',
		};
	}
	if (label === 'medium') {
		return {
			icon: <Circle sx={{ color: '#388e3c', fontSize: '18px' }} />,
			name: 'Medium',
			color: '#388e3c',
		};
	}
	if (label === 'low') {
		return {
			icon: <ArrowDownward sx={{ color: '#1976d2', fontSize: '18px' }} />,
			name: 'Low',
			color: '#1976d2',
		};
	}
	return {
		icon: <Circle sx={{ color: '#9e9e9e', fontSize: '18px' }} />,
		name: priorityLabel || 'N/A',
		color: '#9e9e9e',
	};
};

const EmployeeBroadcastWidget = ({ loading, items, workplaceId }: EmployeeBroadcastWidgetProps) => {
	const { push } = useHistory();
	const queryClient = useQueryClient();

	const { mutate: markAsRead } = useMutation(markBroadcastAsRead, {
		onSuccess: () => {
			queryClient.invalidateQueries(['broadcast', workplaceId]);
		},
	});

	const handleViewClick = (
		messageId: number,
		event: React.MouseEvent,
		shouldMarkAsRead: boolean,
	) => {
		event.preventDefault();
		if (shouldMarkAsRead) {
			markAsRead(messageId);
		}
		push(ROUTES.VIEW_BROADCAST.replace(':id', messageId.toString()));
	};

	return (
		<EmployeeWidgetCard
			title="My Broadcasts"
			loading={loading}
			colSpan={7}
			emptyMessage="No records found"
			isEmpty={items.length === 0}
			header={
				<TableRow>
					<TableCell>Priority</TableCell>
					<TableCell>From</TableCell>
					<TableCell>To</TableCell>
					<TableCell>Subject</TableCell>
					<TableCell>Date</TableCell>
					<TableCell>Time</TableCell>
					<TableCell>Read</TableCell>
				</TableRow>
			}
		>
			{items.map((message) => {
				const priorityIcon = getPriorityIcon(message.priority?.label || message.priority?.name);
				const createdFields = getDisplayCreatedFields(message);

				return (
					<TableRow
						key={message.id}
						className="employee-widget-clickable-row"
						onClick={(event) => handleViewClick(message.id, event, true)}
					>
						<TableCell>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
								{priorityIcon.icon}
								<Typography
									sx={{ color: priorityIcon.color, fontSize: '13px', fontWeight: 600 }}
								>
									{priorityIcon.name}
								</Typography>
							</Box>
						</TableCell>
						<TableCell>{getUserFullName(message.sender)}</TableCell>
						<TableCell>{getReceiverName(message)}</TableCell>
						<TableCell>{message.title ?? message.subject}</TableCell>
						<TableCell>{formatDateFromAPI(createdFields.createdDate)}</TableCell>
						<TableCell>{formatTimeFromAPI(createdFields.createdTime)}</TableCell>
						<TableCell onClick={(event) => event.stopPropagation()}>
							<IconButton
								size="small"
								sx={{ color: 'rgb(68, 99, 136)' }}
								onClick={(event) => {
									event.stopPropagation();
									handleViewClick(message.id, event, true);
								}}
							>
								<Visibility style={{ fontSize: '18px' }} />
							</IconButton>
						</TableCell>
					</TableRow>
				);
			})}
		</EmployeeWidgetCard>
	);
};

export default EmployeeBroadcastWidget;
